import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';
import './FeaturesPage.css';

export default function FeaturesPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Hangrily Features — Solo, Couple & Group Restaurant Picker App + Cashback"
        description="Explore Hangrily's features: Solo mode for quick picks, Couple mode for date nights, Group mode for up to 10 friends, plus $1 cashback on every order (up to 10/mo) for Premium members. Real-time swiping, smart 3-round matching algorithm, cuisine/price/mood filters, GPS restaurant discovery, delivery links & more."
        path="/features"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Features', item: 'https://hangrily.app/features' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Hangrily Features',
            description: 'All features of the Hangrily restaurant decision app',
            numberOfItems: 7,
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Smart Restaurant Matching', description: 'Intelligent algorithm with real-time data, location-based search, 12+ cuisine types, price filtering, and mood-based recommendations.' },
              { '@type': 'ListItem', position: 2, name: 'Tinder-Style Swiping', description: 'Familiar swipe interface with three options: Dislike, Like, and Love. Visual restaurant cards with photos, ratings, and key info.' },
              { '@type': 'ListItem', position: 3, name: 'Couple Mode', description: 'Both partners swipe independently and match on mutual favorites. Auto-join sessions, hunger nudges, and shared history.' },
              { '@type': 'ListItem', position: 4, name: 'Group Mode', description: 'Up to 10 participants join via session code. Democratic voting with majority rules and real-time updates.' },
              { '@type': 'ListItem', position: 5, name: 'Cashback Rewards', description: 'Earn a flat $1 cashback on every verified food order, up to 10 orders per month. Just upload a receipt or order confirmation photo.' },
              { '@type': 'ListItem', position: 6, name: 'Analytics & History', description: 'Decision history, favorite restaurants, cuisine breakdown, swipe statistics, and partner stats.' },
              { '@type': 'ListItem', position: 7, name: 'Integrated Directions', description: 'One-tap navigation to your matched restaurant with full address and distance information.' }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Features That End Arguments</h1>
          <p className="hero-subtitle">
            Everything you need to make restaurant decisions fast, fair, and fun.
          </p>
        </section>

        <section className="features-showcase">
          <div className="feature-showcase-item" style={{ background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' }}>
            <div className="showcase-content">
              <h2>🤖 AI Smart Search</h2>
              <p className="feature-lead">
                Skip the filters. Just tell us what you want in plain English and AI finds the perfect match.
              </p>
              <ul className="feature-benefits">
                <li><strong>Natural Language:</strong> "I want spicy tacos under $15 within 3 miles"</li>
                <li><strong>Instant Understanding:</strong> AI parses cuisine, price, distance, and mood from your text</li>
                <li><strong>Smart Matching:</strong> Returns only restaurants that match your exact request</li>
                <li><strong>No Filters Needed:</strong> Faster than setting preferences manually</li>
                <li><strong>Works for Anything:</strong> Quick lunch, date night, budget meals, or fancy dinners</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="feature-demo-card" style={{ padding: '20px' }}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>💬 You type:</div>
                  <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '8px', fontSize: '15px', marginBottom: '16px' }}>
                    "I want spicy tacos under $15"
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>🤖 AI finds:</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#667eea', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '13px' }}>Mexican</span>
                    <span style={{ background: '#667eea', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '13px' }}>$-$$</span>
                    <span style={{ background: '#667eea', color: 'white', padding: '6px 12px', borderRadius: '20px', fontSize: '13px' }}>Nearby</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-showcase-item">
            <div className="showcase-content">
              <h2>🎯 Smart Restaurant Matching</h2>
              <p className="feature-lead">
                Our intelligent algorithm learns your preferences and finds restaurants you'll actually love.
              </p>
              <ul className="feature-benefits">
                <li><strong>Real-time Data:</strong> Up-to-date ratings, photos, hours, and reviews from verified sources</li>
                <li><strong>Location-Based:</strong> Find restaurants within 1, 3, 5, or 10 miles of your current location</li>
                <li><strong>Cuisine Variety:</strong> 12+ cuisine types from Mexican to Vietnamese</li>
                <li><strong>Price Filtering:</strong> Budget-friendly to fine dining options</li>
                <li><strong>Mood-Based:</strong> Filter by spicy, comfort food, healthy, quick, fancy, or adventurous</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="feature-demo-card">
                <div className="demo-restaurant">
                  <div className="demo-image">🍕</div>
                  <h3>Pizza Palace</h3>
                  <div className="demo-meta">
                    <span>Italian</span>
                    <span>$$</span>
                    <span>⭐ 4.5</span>
                  </div>
                  <p>0.8 miles away</p>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-showcase-item reverse">
            <div className="showcase-content">
              <h2>👆 Tinder-Style Swiping</h2>
              <p className="feature-lead">
                Familiar, intuitive interface that makes decision-making fun instead of frustrating.
              </p>
              <ul className="feature-benefits">
                <li><strong>Three Swipe Options:</strong> Dislike (👎), Like (👍), or Love (❤️)</li>
                <li><strong>Visual Cards:</strong> See restaurant photos, ratings, and key info at a glance</li>
                <li><strong>Quick Decisions:</strong> Swipe through 15 restaurants in under 2 minutes</li>
                <li><strong>Progress Tracking:</strong> Always know how many restaurants are left</li>
                <li><strong>Round System:</strong> 3 rounds maximum to find your perfect match</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="swipe-buttons-demo">
                <button className="demo-swipe-btn dislike">👎<br/>Dislike</button>
                <button className="demo-swipe-btn like">👍<br/>Like</button>
                <button className="demo-swipe-btn love">❤️<br/>Love</button>
              </div>
            </div>
          </div>

          <div className="feature-showcase-item">
            <div className="showcase-content">
              <h2>💑 Couple Mode</h2>
              <p className="feature-lead">
                The #1 feature for couples who are tired of the "where should we eat?" argument.
              </p>
              <ul className="feature-benefits">
                <li><strong>One-Time Setup:</strong> Link your partner's account via email - never enter it again</li>
                <li><strong>Auto-Join Sessions:</strong> Partner automatically added when you create a session</li>
                <li><strong>Simultaneous Swiping:</strong> Both swipe at the same time on your own devices</li>
                <li><strong>Fair Algorithm:</strong> Both preferences weighted equally - no one dominates</li>
                <li><strong>Hunger Nudges:</strong> Send playful notifications when you're ready to eat</li>
                <li><strong>Shared History:</strong> Track all your dining decisions together</li>
                <li><strong>Only Mutual Matches:</strong> Only see restaurants you BOTH liked</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="couple-demo">
                <div className="partner-card">
                  <div className="partner-avatar">👤</div>
                  <p>You</p>
                  <div className="swipe-status">Swiping...</div>
                </div>
                <div className="heart-connector">❤️</div>
                <div className="partner-card">
                  <div className="partner-avatar">👤</div>
                  <p>Partner</p>
                  <div className="swipe-status">Swiping...</div>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-showcase-item reverse">
            <div className="showcase-content">
              <h2>👥 Group Mode</h2>
              <p className="feature-lead">
                Perfect for friend groups, family dinners, or team outings. Democracy in action.
              </p>
              <ul className="feature-benefits">
                <li><strong>Up to 10 Participants:</strong> Handle small groups to large parties</li>
                <li><strong>Easy Session Codes:</strong> Share a 6-digit code (e.g., ABC123) to join</li>
                <li><strong>Democratic Voting:</strong> Every person's vote counts equally</li>
                <li><strong>Majority Rules:</strong> Restaurants need 50%+ approval to advance</li>
                <li><strong>Real-Time Updates:</strong> See when others finish swiping each round</li>
                <li><strong>Group Consensus:</strong> Algorithm finds restaurants most people agree on</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="group-demo">
                <div className="session-code-display">
                  <p>Session Code</p>
                  <h3>ABC123</h3>
                  <p className="participants-count">5 participants</p>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-showcase-item" style={{ background: 'linear-gradient(135deg, #10b98115 0%, #05966915 100%)' }}>
            <div className="showcase-content">
              <h2>💰 Get Paid to Eat: Cashback Rewards</h2>
              <p className="feature-lead">
                Premium members earn real cash back on every order — dine-in, takeout, or delivery.
              </p>
              <ul className="feature-benefits">
                <li><strong>Flat $1 Per Order:</strong> Simple, guaranteed cashback — no confusing tiers</li>
                <li><strong>Up to 10 Orders/Month:</strong> Earn up to $10 back every month</li>
                <li><strong>Just Snap a Receipt:</strong> Upload a photo of your receipt or order confirmation</li>
                <li><strong>Fast Verification:</strong> Most orders auto-approve instantly</li>
                <li><strong>Flexible Redemption:</strong> Cash out for gift cards or subscription credit</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="feature-demo-card" style={{ padding: '20px' }}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: '#059669' }}>+$1.00</div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>Cashback earned per order</div>
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#059669', fontWeight: '600' }}>Up to $10/month</div>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-showcase-item">
            <div className="showcase-content">
              <h2>📊 Analytics & History</h2>
              <p className="feature-lead">
                Track your dining decisions and discover your food preferences over time.
              </p>
              <ul className="feature-benefits">
                <li><strong>Decision History:</strong> See all past restaurant choices with dates</li>
                <li><strong>Favorite Restaurants:</strong> Track which places you visit most</li>
                <li><strong>Cuisine Breakdown:</strong> Visualize your top cuisine preferences</li>
                <li><strong>Swipe Statistics:</strong> Total swipes, decisions made, and more</li>
                <li><strong>Partner Stats:</strong> See your dining patterns as a couple</li>
                <li><strong>Export Data:</strong> Download your history for personal records</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="analytics-demo">
                <div className="stat-box">
                  <div className="stat-number">247</div>
                  <div className="stat-label">Total Swipes</div>
                </div>
                <div className="stat-box">
                  <div className="stat-number">42</div>
                  <div className="stat-label">Decisions Made</div>
                </div>
                <div className="stat-box">
                  <div className="stat-number">8</div>
                  <div className="stat-label">Cuisines Tried</div>
                </div>
              </div>
            </div>
          </div>

          <div className="feature-showcase-item reverse">
            <div className="showcase-content">
              <h2>🗺️ Integrated Directions</h2>
              <p className="feature-lead">
                Once you've found your match, get there fast with one-tap directions.
              </p>
              <ul className="feature-benefits">
                <li><strong>Map Integration:</strong> Open directions directly in your maps app</li>
                <li><strong>Distance Display:</strong> See how far each restaurant is before swiping</li>
                <li><strong>Address Information:</strong> Full address displayed on result screen</li>
                <li><strong>One-Tap Navigation:</strong> Start navigating with a single button press</li>
                <li><strong>Alternative Routes:</strong> Multiple route options available</li>
              </ul>
            </div>
            <div className="showcase-visual">
              <div className="directions-demo">
                <div className="map-placeholder">
                  <div className="map-pin">📍</div>
                  <p>Your Location</p>
                  <div className="route-line"></div>
                  <div className="map-pin">🍽️</div>
                  <p>Restaurant</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="additional-features">
          <h2 className="section-title">Even More Features</h2>
          <div className="features-grid-compact">
            <div className="compact-feature">
              <span className="feature-icon-sm">⚡</span>
              <h3>Lightning Fast</h3>
              <p>Most sessions complete in under 3 minutes</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">🔒</span>
              <h3>Privacy First</h3>
              <p>Your data is encrypted and never sold</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">📱</span>
              <h3>Cross-Platform</h3>
              <p>Works on web, iOS, and Android</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">🆓</span>
              <h3>Generous Free Tier</h3>
              <p>Get started free with no credit card required</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">💰</span>
              <h3>$1 Cashback Per Order</h3>
              <p>Up to 10 orders/month with Premium — upload a receipt to get paid</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">🚫</span>
              <h3>No Ads</h3>
              <p>Clean interface, zero advertisements</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">💾</span>
              <h3>Save Favorites</h3>
              <p>Bookmark restaurants for future sessions</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">🔄</span>
              <h3>Restart Anytime</h3>
              <p>Don't like the results? Start over with new preferences</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">📤</span>
              <h3>Easy Sharing</h3>
              <p>Share results via text, email, or social media</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">🌙</span>
              <h3>Dark Theme</h3>
              <p>Sleek dark UI that's easy on the eyes</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">🔔</span>
              <h3>Smart Notifications</h3>
              <p>Get notified when your partner starts a session</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">⏱️</span>
              <h3>Session Timer</h3>
              <p>See how long each decision took</p>
            </div>
            <div className="compact-feature">
              <span className="feature-icon-sm">🎨</span>
              <h3>Beautiful UI</h3>
              <p>Modern, intuitive design that's a joy to use</p>
            </div>
          </div>
        </section>

        <section className="comparison-section">
          <h2 className="section-title">Why Hangrily Beats the Alternatives</h2>
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell"></div>
              <div className="comparison-cell highlight">Hangrily</div>
              <div className="comparison-cell">Other Apps</div>
            </div>
            <div className="comparison-row">
              <div className="comparison-cell"><strong>Decision Speed</strong></div>
              <div className="comparison-cell highlight">✅ 2-5 minutes</div>
              <div className="comparison-cell">❌ 15-30 minutes</div>
            </div>
            <div className="comparison-row">
              <div className="comparison-cell"><strong>Couple Mode</strong></div>
              <div className="comparison-cell highlight">✅ Built-in</div>
              <div className="comparison-cell">❌ Not available</div>
            </div>
            <div className="comparison-row">
              <div className="comparison-cell"><strong>Group Sessions</strong></div>
              <div className="comparison-cell highlight">✅ Up to 10 people</div>
              <div className="comparison-cell">⚠️ Limited or none</div>
            </div>
            <div className="comparison-row">
              <div className="comparison-cell"><strong>Price</strong></div>
              <div className="comparison-cell highlight">✅ Free tier + affordable Premium</div>
              <div className="comparison-cell">❌ $5-15/month required</div>
            </div>
            <div className="comparison-row">
              <div className="comparison-cell"><strong>Cashback</strong></div>
              <div className="comparison-cell highlight">✅ $1/order, up to 10/mo</div>
              <div className="comparison-cell">❌ Not available</div>
            </div>
            <div className="comparison-row">
              <div className="comparison-cell"><strong>Ads</strong></div>
              <div className="comparison-cell highlight">✅ Zero ads</div>
              <div className="comparison-cell">❌ Constant ads</div>
            </div>
            <div className="comparison-row">
              <div className="comparison-cell"><strong>Real-time Data</strong></div>
              <div className="comparison-cell highlight">✅ Real-time verified data</div>
              <div className="comparison-cell">⚠️ Outdated databases</div>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Experience These Features Today</h2>
          <p>No credit card required to get started. Better restaurant decisions in minutes.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Try It Free — No Account Needed
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
